import type { i18n as I18nInstance } from "i18next";

export const LANGUAGE_COOKIE_NAME = "boona_locale";
export const MAIN_LANGUAGE_STORAGE_KEY = "i18n_language";
export const WIDGET_LANGUAGE_STORAGE_KEY = "widget_i18n_language";
const LANGUAGE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const SHARED_LANGUAGE_COOKIE_DOMAIN = ".boona.app";

export const LANGUAGE_OPTIONS = [
  { code: "en", label: "English" },
  { code: "ru", label: "Русский" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "pl", label: "Polski" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "sv", label: "Svenska" },
  { code: "da", label: "Dansk" },
  { code: "no", label: "Norsk" },
  { code: "ar", label: "العربية" },
  { code: "bg", label: "Български" },
  { code: "hr", label: "Hrvatski" },
  { code: "cs", label: "Čeština" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "hu", label: "Magyar" },
  { code: "tr", label: "Türkçe" },
  { code: "zh", label: "简体中文" },
  { code: "uk", label: "Українська" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "fi", label: "Suomi" },
] as const;

const SUPPORTED_LANGUAGE_CODES = new Set(LANGUAGE_OPTIONS.map((lang) => lang.code));

export function normalizeLanguageCode(value?: string | null): string | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGE_CODES.has(normalized) ? normalized : null;
}

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function getLanguageCookieDomain(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname.toLowerCase();
  return hostname === "boona.app" || hostname.endsWith(".boona.app")
    ? SHARED_LANGUAGE_COOKIE_DOMAIN
    : null;
}

function getCookieSecurityAttributes(): string[] {
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    return ["Secure"];
  }
  return [];
}

function expireCookie(name: string, domain?: string): void {
  if (typeof document === "undefined") return;
  const parts = [
    `${name}=`,
    "path=/",
    "expires=Thu, 01 Jan 1970 00:00:00 GMT",
    "SameSite=Lax",
    ...getCookieSecurityAttributes(),
  ];
  if (domain) {
    parts.push(`domain=${domain}`);
  }
  document.cookie = parts.join("; ");
}

export function writeCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    "path=/",
    `max-age=${LANGUAGE_COOKIE_MAX_AGE}`,
    "SameSite=Lax",
    ...getCookieSecurityAttributes(),
  ];
  const domain = getLanguageCookieDomain();
  if (domain) {
    parts.push(`domain=${domain}`);
  }
  document.cookie = parts.join("; ");
}

function clearHostScopedLanguageCookie(name: string): void {
  if (typeof window === "undefined") return;
  const hostname = window.location.hostname;
  const sharedDomain = getLanguageCookieDomain();
  const sharedHostname = sharedDomain?.replace(/^\./, "") ?? null;
  const hostDomainVariants = new Set<string>();

  if (hostname && hostname !== sharedHostname) {
    hostDomainVariants.add(hostname);
    hostDomainVariants.add(`.${hostname}`);
  }

  if (hostname.startsWith("www.")) {
    const withoutWww = hostname.slice(4);
    if (withoutWww && withoutWww !== sharedHostname) {
      hostDomainVariants.add(withoutWww);
      hostDomainVariants.add(`.${withoutWww}`);
    }
  }

  expireCookie(name);

  for (const domain of hostDomainVariants) {
    expireCookie(name, domain);
  }
}

export function migrateLanguageCookieToSharedDomain(localStorageKey?: string): void {
  const normalized = readLanguageCookie();
  if (!normalized) return;

  writeCookie(LANGUAGE_COOKIE_NAME, normalized);
  clearHostScopedLanguageCookie(LANGUAGE_COOKIE_NAME);

  if (typeof window !== "undefined" && localStorageKey) {
    window.localStorage.setItem(localStorageKey, normalized);
  }
}

export function readLanguageCookie(): string | null {
  return normalizeLanguageCode(readCookie(LANGUAGE_COOKIE_NAME));
}

export function readQueryLanguage(): string | null {
  if (typeof window === "undefined") return null;
  return normalizeLanguageCode(new URLSearchParams(window.location.search).get("lang"));
}

export function persistLanguagePreference(langCode: string, localStorageKey?: string): void {
  const normalized = normalizeLanguageCode(langCode);
  if (!normalized) return;

  writeCookie(LANGUAGE_COOKIE_NAME, normalized);
  clearHostScopedLanguageCookie(LANGUAGE_COOKIE_NAME);

  if (typeof window !== "undefined" && localStorageKey) {
    window.localStorage.setItem(localStorageKey, normalized);
  }
}

export async function changeLanguageAndPersist(
  i18n: I18nInstance,
  langCode: string,
  localStorageKey?: string,
): Promise<void> {
  const normalized = normalizeLanguageCode(langCode);
  if (!normalized) return;

  await i18n.changeLanguage(normalized);
  persistLanguagePreference(normalized, localStorageKey);
}
